import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { ChangeLogsResponseDataSchema } from "schema"
import z from "zod"
import { ChangeLogEntry } from "./ChangeLogEntry"
import { ChangeLogValuesByModel } from "./index"

type ChangeLog = z.infer<typeof ChangeLogsResponseDataSchema>[number]

type Props<T extends keyof ChangeLogValuesByModel> = {
  changeLogs: ChangeLog[]
  onApplyChanges?: (snapshot: ChangeLogValuesByModel[T]) => Promise<void>
  canApplyChanges?: (snapshot: ChangeLogValuesByModel[T]) => boolean
  fieldsMap?: Array<{ field: keyof ChangeLogValuesByModel[T]; label: string }>
  valueFormatter?: (key: string, value: unknown) => string | null
}

const formatSnapshotKeyLabel = (key: string) => {
  const withSpaces = key
    .replace(/_/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .toLowerCase()

  return withSpaces.replace(/\b\w/g, (char) => char.toUpperCase())
}

export function ChangeLogTimeline<T extends keyof ChangeLogValuesByModel>({
  changeLogs,
  onApplyChanges,
  canApplyChanges,
  fieldsMap,
  valueFormatter,
}: Props<T>) {
  const { t } = useTranslation()

  const processedLogs = useMemo(() => {
    return changeLogs.map((log) => {
      const snapshot = log.snapshot as Record<string, unknown>
      const snapshotKeys = Object.keys(snapshot ?? {})
        .filter((key) => snapshot[key] !== null || snapshot[key] !== undefined)
        .sort(
          (a, b) =>
            (fieldsMap?.findIndex((field) => field.field === a) ?? -1) -
            (fieldsMap?.findIndex((field) => field.field === b) ?? -1),
        )
      const snapshotKeysMap = snapshotKeys.map((key) => {
        const mappedLabel = fieldsMap?.find((field) => field.field === key)?.label

        return {
          label: mappedLabel ?? formatSnapshotKeyLabel(key),
          key,
        }
      })

      let actorDisplay: React.ReactNode
      if (log.adminId) {
        actorDisplay = <span className="text-sm font-medium">{"Permut"}</span>
      } else if (log.employeeName || log.employeeEmail) {
        const displayName = log.employeeName || log.employeeEmail || ""
        const displayEmail = log.employeeEmail && log.employeeName ? log.employeeEmail : null
        actorDisplay = (
          <span className="text-sm font-medium">
            {displayName}
            {displayEmail && <span className="text-muted-foreground">{` (${displayEmail})`}</span>}
          </span>
        )
      } else {
        actorDisplay = (
          <span className="text-sm text-muted-foreground">{t("shared.changeHistory.unknown")}</span>
        )
      }

      return {
        ...log,
        snapshot,
        snapshotKeysMap,
        actorDisplay,
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changeLogs, JSON.stringify(fieldsMap), t])

  return (
    <div className="relative mx-auto max-w-full">
      <div className="absolute top-2 left-2 bottom-4 w-[1px] bg-muted" />
      <div className="flex flex-col gap-6">
        {processedLogs.map((log) => (
          <ChangeLogEntry
            key={log.id}
            log={log}
            onApplyChanges={onApplyChanges}
            canApplyChanges={canApplyChanges}
            valueFormatter={valueFormatter}
          />
        ))}
      </div>
    </div>
  )
}
